import { useCallback, useEffect, useRef } from "react";
import {
  productUsageStore,
  ProductUsageView,
} from "features/ProductUsage/ProductUsageStore";

const PAGE_SIZE = 25;

/**
 * Infinite-scroll pagination for the product-usage drill-down. Returns the
 * list slice for the active view (buildings vs components) together with a
 * `fetchData(start)` handler that appends pages, matching the shared Table's
 * fetch contract (see MaterialSearch).
 */
export default function useProductUsagePagination(
  id: string,
  view: ProductUsageView
) {
  const usageList =
    view === "buildings"
      ? productUsageStore.buildings
      : productUsageStore.components;

  // Tracks the params of the latest active fetch so responses for a stale
  // id/view can be discarded instead of overwriting current data.
  const activeParams = useRef({ id, view });
  useEffect(() => {
    activeParams.current = { id, view };
  }, [id, view]);

  const fetchData = useCallback(
    async (start: number) => {
      const pageNumber = start / PAGE_SIZE + 1;

      if (view === "buildings") {
        // start === 0 is a fresh fetch (new id/view): clear immediately so
        // stale rows aren't shown while the first page loads.
        productUsageStore.setBuildings({
          count: start ? productUsageStore.buildings.count : 0,
          items: start ? productUsageStore.buildings.items : [],
          loading: true,
        });
        const data = await productUsageStore.fetchBuildings(
          id,
          pageNumber,
          PAGE_SIZE
        );
        // Discard if the id/view changed while this page was in flight.
        if (
          activeParams.current.id !== id ||
          activeParams.current.view !== view
        )
          return;
        productUsageStore.setBuildings({
          count: data?.count ?? 0,
          items: [
            ...(start ? productUsageStore.buildings.items : []),
            ...(data?.items ?? []),
          ],
          loading: false,
        });
        return;
      }

      productUsageStore.setComponents({
        count: start ? productUsageStore.components.count : 0,
        items: start ? productUsageStore.components.items : [],
        loading: true,
      });
      const data = await productUsageStore.fetchComponents(
        id,
        pageNumber,
        PAGE_SIZE
      );
      // Discard if the id/view changed while this page was in flight.
      if (activeParams.current.id !== id || activeParams.current.view !== view)
        return;
      productUsageStore.setComponents({
        count: data?.count ?? 0,
        items: [
          ...(start ? productUsageStore.components.items : []),
          ...(data?.items ?? []),
        ],
        loading: false,
      });
    },
    [id, view]
  );

  return { list: usageList, fetchData };
}
